// client/pages/History.tsx
// Shows deposits, withdrawals, and DEX swaps as separate categories.
// Swaps are never counted as deposits or withdrawals.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import {
  ArrowUpRight,
  ArrowDownLeft,
  TrendingUp,
  ArrowLeft,
  CheckCircle,
  Copy,
  RefreshCw,
  History as HistoryIcon,
  Clock,
  Wallet,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  ArrowLeftRight,
} from "lucide-react";

type TxType = "deposit" | "withdraw" | "trade" | "p2p" | "swap";

type Transaction = {
  id: string;
  type: TxType;
  token: string;
  amount: number;
  status: "completed";
  timestamp: string;
  from_address?: string;
  to_address?: string;
  network?: string;
  txHash?: string;
  transactionId?: string;
  source?: "kv" | "blockchain" | "internal";
  chain?: string;
  // Swap-specific fields (populated only when type === "swap")
  swapFromSymbol?: string;
  swapToSymbol?: string;
  swapFromAmount?: number;
  swapToAmount?: number;
};

const CHAIN_DISPLAY_NAMES: Record<string, string> = {
  solana: "SOLANA",
  ethereum: "ETHEREUM",
  bsc: "BSC",
  polygon: "POLYGON",
};

const HistoryInstructions = () => {
  return (
    <div className="bg-card rounded-xl p-6 border border-border h-full">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
          <HistoryIcon className="w-4 h-4 text-black" />
        </div>
        <h2 className="text-[10px] font-bold uppercase tracking-wider text-yellow-400">
          History Info
        </h2>
      </div>
      <div className="space-y-4">
        <div className="space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            TRANSACTION CATEGORIES
          </p>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <ArrowDownLeft className="w-3 h-3 text-green-400" />
              <p className="text-[10px] uppercase text-foreground">
                Deposits — funds added to your wallet
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ArrowUpRight className="w-3 h-3 text-red-400" />
              <p className="text-[10px] uppercase text-foreground">
                Withdrawals — funds sent to an external wallet
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ArrowLeftRight className="w-3 h-3 text-blue-400" />
              <p className="text-[10px] uppercase text-foreground">
                Swaps — token-to-token DEX trades
              </p>
            </div>
          </div>
        </div>
        <div className="border-t border-border my-2"></div>
        <div className="space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            SWAPS ARE SEPARATE
          </p>
          <p className="text-[10px] text-foreground uppercase">
            DEX swaps move value between two tokens. They are never counted as
            deposits or withdrawals, and never affect your P2P order history.
          </p>
        </div>
        <div className="border-t border-border my-2"></div>
        <div className="space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            VERIFIED ONLY
          </p>
          <div className="flex items-center gap-2 upppercase">
            <CheckCircle className="w-3 h-3 text-yellow-400" />
            <p className="text-[10px] text-foreground uppercase">
              Only verified on-chain transactions are displayed
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function History() {
  const [user, setUser] = useState<any | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch("/api/auth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            setUser(data.user);
            return;
          }
        }
        navigate("/login");
      } catch (error) {
        console.error("[History] Auth error:", error);
        navigate("/login");
      }
    };
    fetchUser();
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    fetchAllTransactions();
  }, [user]);

  async function fetchAllTransactions() {
    if (!user) return;
    setLoading(true);
    try {
      // Fetch all three sources in parallel.
      const [depositsRes, withdrawalsRes, swapsRes] = await Promise.all([
        fetch(`/api/wallet?action=verified-deposits`, { credentials: "include" }),
        fetch(`/api/wallet?action=verified-withdrawals`, { credentials: "include" }),
        fetch(`/api/wallet?action=verified-swaps`, { credentials: "include" }),
      ]);

      const depositsData = await depositsRes.json().catch(() => ({ success: false }));
      const withdrawalsData = await withdrawalsRes.json().catch(() => ({ success: false }));
      const swapsData = await swapsRes.json().catch(() => ({ success: false }));

      const allTransactions: Transaction[] = [];

      // ─── Deposits ──────────────────────────────────────────
      if (depositsData.success && Array.isArray(depositsData.transactions)) {
        depositsData.transactions.forEach((tx: any) => {
          allTransactions.push({
            id: tx.transactionId || `deposit_${tx.timestamp}`,
            type: "deposit",
            token: tx.token || "SOL",
            amount: parseFloat(tx.amount),
            status: "completed",
            timestamp: tx.timestamp,
            from_address: tx.fromAddress,
            to_address: tx.toAddress,
            txHash: tx.transactionHash,
            transactionId: tx.transactionId,
            chain: tx.chain || "solana",
          });
        });
      }

      // ─── Withdrawals ───────────────────────────────────────
      if (withdrawalsData.success && Array.isArray(withdrawalsData.transactions)) {
        withdrawalsData.transactions.forEach((tx: any) => {
          allTransactions.push({
            id: tx.transactionId || `withdraw_${tx.timestamp}`,
            type: "withdraw",
            token: tx.token || "SOL",
            amount: parseFloat(tx.amount),
            status: "completed",
            timestamp: tx.timestamp,
            from_address: tx.fromAddress,
            to_address: tx.toAddress,
            txHash: tx.transactionHash,
            transactionId: tx.transactionId,
            chain: tx.chain || "solana",
          });
        });
      }

      // ─── DEX Swaps (never mixed with deposits/withdrawals) ─
      if (swapsData.success && Array.isArray(swapsData.transactions)) {
        swapsData.transactions.forEach((tx: any) => {
          // Skip any record that is somehow typed as deposit/withdraw
          const kind = String(tx.type || tx.kind || "").toLowerCase();
          if (kind === "deposit" || kind === "withdraw" || kind === "withdrawal") {
            return;
          }

          allTransactions.push({
            id: tx.transactionId || tx.signature || `swap_${tx.timestamp}`,
            type: "swap",
            token: tx.token || tx.swapFromSymbol || "TOKEN",
            amount: parseFloat(tx.amount || tx.swapFromAmount || "0"),
            status: "completed",
            timestamp: tx.timestamp,
            from_address: tx.fromAddress,
            to_address: tx.toAddress,
            txHash: tx.transactionHash || tx.signature,
            transactionId: tx.transactionId,
            chain: tx.chain || "solana",
            swapFromSymbol: tx.swapFromSymbol,
            swapToSymbol: tx.swapToSymbol,
            swapFromAmount: tx.swapFromAmount ? parseFloat(tx.swapFromAmount) : undefined,
            swapToAmount: tx.swapToAmount ? parseFloat(tx.swapToAmount) : undefined,
          });
        });
      }

      // ─── Dedupe by txHash / transactionId ──────────────────
      const uniqueMap = new Map<string, Transaction>();
      allTransactions.forEach((tx) => {
        const key =
          tx.txHash ||
          tx.transactionId ||
          `${tx.type}_${tx.timestamp}_${tx.chain}`;
        if (!uniqueMap.has(key)) uniqueMap.set(key, tx);
      });

      const sorted = Array.from(uniqueMap.values()).sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      setTransactions(sorted);
    } catch (err) {
      console.error("[History] Error fetching transactions:", err);
      setTransactions([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAllTransactions();
  };

  const getTransactionIcon = (type: TxType) => {
    switch (type) {
      case "deposit":
        return <ArrowDownLeft className="w-6 h-6 text-green-400" strokeWidth={2.5} />;
      case "withdraw":
        return <ArrowUpRight className="w-6 h-6 text-red-400" strokeWidth={2.5} />;
      case "swap":
        return <ArrowLeftRight className="w-6 h-6 text-blue-400" strokeWidth={2.5} />;
      default:
        return <TrendingUp className="w-6 h-6 text-muted-foreground" strokeWidth={2.5} />;
    }
  };

  const getExplorerUrl = (txHash: string, chain: string) => {
    if (!txHash) return null;
    switch (chain) {
      case "solana":
        return `https://solscan.io/tx/${txHash}`;
      case "ethereum":
        return `https://etherscan.io/tx/${txHash}`;
      case "bsc":
        return `https://bscscan.com/tx/${txHash}`;
      case "polygon":
        return `https://polygonscan.com/tx/${txHash}`;
      default:
        return null;
    }
  };

  const formatAmount = (tx: Transaction) => {
    // SWAP shows "X → Y" instead of +amount / -amount
    if (tx.type === "swap") {
      if (tx.swapFromSymbol && tx.swapToSymbol) {
        const fromAmt =
          tx.swapFromAmount !== undefined
            ? formatNumber(tx.swapFromAmount, tx.swapFromSymbol)
            : formatNumber(tx.amount, tx.token);
        const toAmt =
          tx.swapToAmount !== undefined
            ? formatNumber(tx.swapToAmount, tx.swapToSymbol)
            : "?";
        return `${fromAmt} ${tx.swapFromSymbol} → ${toAmt} ${tx.swapToSymbol}`;
      }
      return `${formatNumber(tx.amount, tx.token)} ${tx.token}`;
    }

    // DEPOSIT / WITHDRAW show sign + number
    const prefix = tx.type === "withdraw" ? "-" : "+";
    return `${prefix}${formatNumber(tx.amount, tx.token)} ${tx.token}`;
  };

  const formatNumber = (amount: number, token: string): string => {
    if (token === "SOL" || token === "ETH" || token === "BNB" || token === "MATIC")
      return amount.toFixed(4);
    if (token === "USDC" || token === "USDT") return amount.toFixed(2);
    if (["FIXERCOIN", "PINGX", "LOCKER", "FXM", "FIXORIUM"].includes(token))
      return amount.toFixed(6);
    return amount.toFixed(6);
  };

  const getTypeLabel = (type: TxType): string => {
    switch (type) {
      case "deposit":
        return "DEPOSIT";
      case "withdraw":
        return "WITHDRAW";
      case "swap":
        return "SWAP";
      default:
        return type.toUpperCase();
    }
  };

  const getTypeColor = (type: TxType): string => {
    switch (type) {
      case "deposit":
        return "text-green-400";
      case "withdraw":
        return "text-red-400";
      case "swap":
        return "text-blue-400";
      default:
        return "text-muted-foreground";
    }
  };

  if (!user) return null;
    const HistoryContent = () => {
    const visibleTransactions = showAll ? transactions : transactions.slice(0, 4);
    const hasMore = transactions.length > 4;

    return (
      <div className="bg-card rounded-xl p-6 border border-border h-full flex flex-col">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 hover:bg-muted rounded-lg transition"
            >
              <ArrowLeft className="w-4 h-4 text-foreground" />
            </button>
            <div>
              <h1 className="text-[12px] font-bold uppercase tracking-wider text-yellow-400">
                ON-CHAIN HISTORY
              </h1>
              <p className="text-[8px] text-muted-foreground mt-1 uppercase">
                VERIFIED BLOCKCHAIN TRANSACTIONS
              </p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1 text-[8px] text-yellow-400 hover:text-yellow-500 transition uppercase"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "REFRESHING..." : "REFRESH"}
          </button>
        </div>
        <div className="w-full space-y-4 flex-1">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-yellow-400" />
              <div className="text-[8px] text-muted-foreground uppercase">
                LOADING TRANSACTIONS...
              </div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8">
              <Wallet className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <div className="text-[8px] text-muted-foreground uppercase">
                NO TRANSACTIONS FOUND
              </div>
              <p className="text-[8px] text-muted-foreground mt-2">
                Deposits, withdrawals, and swaps will appear here
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <div className="grid grid-cols-4 gap-4 pb-2 text-[8px] text-muted-foreground font-semibold uppercase border-b border-border">
                  <div>TRANSACTION</div>
                  <div className="text-right">AMOUNT</div>
                  <div className="text-right">NETWORK</div>
                  <div className="text-right">TYPE</div>
                </div>
                {visibleTransactions.map((tx) => {
                  const explorerUrl = getExplorerUrl(
                    tx.txHash || "",
                    tx.chain || "solana"
                  );
                  const typeLabel = getTypeLabel(tx.type);
                  const typeColor = getTypeColor(tx.type);

                  return (
                    <div
                      key={
                        tx.id ||
                        tx.transactionId ||
                        `${tx.type}_${tx.timestamp}_${tx.chain}`
                      }
                      className="grid grid-cols-4 gap-4 py-3 rounded-lg hover:bg-muted transition-all items-center"
                    >
                      {/* Column 1 — icon + time + counterparty/tx */}
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                          {getTransactionIcon(tx.type)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[8px] text-muted-foreground uppercase">
                            {new Date(tx.timestamp).toLocaleString()}
                          </div>
                          <div className="text-[7px] text-muted-foreground mt-0.5 flex items-center gap-1">
                            {tx.type === "deposit" && tx.from_address
                              ? `From: ${tx.from_address.slice(0, 4)}...${tx.from_address.slice(-4)}`
                              : tx.type === "withdraw" && tx.to_address
                              ? `To: ${tx.to_address.slice(0, 4)}...${tx.to_address.slice(-4)}`
                              : tx.type === "swap" && tx.swapFromSymbol && tx.swapToSymbol
                              ? `Swap ${tx.swapFromSymbol} → ${tx.swapToSymbol}`
                              : ""}
                            {tx.txHash && explorerUrl && (
                              <a
                                href={explorerUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-yellow-400"
                              >
                                <ExternalLink className="w-2.5 h-2.5" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Column 2 — amount */}
                      <div className="text-right">
                        <div className="font-semibold text-[9px] text-foreground whitespace-nowrap">
                          {formatAmount(tx)}
                        </div>
                      </div>

                      {/* Column 3 — network */}
                      <div className="text-right">
                        <div className="text-[7px] font-semibold uppercase text-muted-foreground">
                          {CHAIN_DISPLAY_NAMES[tx.chain || "solana"] || "SOLANA"}
                        </div>
                      </div>

                      {/* Column 4 — type label */}
                      <div className="text-right">
                        <div className={`text-[8px] font-semibold uppercase ${typeColor}`}>
                          {typeLabel}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {hasMore && (
                <>
                  <div className="border-t border-border my-2"></div>
                  <button
                    onClick={() => setShowAll(!showAll)}
                    className="w-full flex items-center justify-center gap-2 text-[9px] uppercase text-yellow-400 hover:text-yellow-500 transition py-2"
                  >
                    {showAll ? (
                      <>
                        SHOW LESS <ChevronUp className="w-3 h-3" />
                      </>
                    ) : (
                      <>
                        MORE DETAIL <ChevronDown className="w-3 h-3" />
                      </>
                    )}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {isDesktop ? (
        <div className="flex gap-[3px] p-6 min-h-screen">
          <div className="w-1/2">
            <HistoryInstructions />
          </div>
          <div className="w-1/2">
            <HistoryContent />
          </div>
        </div>
      ) : (
        <div className="w-full min-h-screen bg-background text-foreground px-[5px] py-6 space-y-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/dashboard")}
                className="p-2 hover:bg-muted rounded-lg transition"
              >
                <ArrowLeft className="w-5 h-5 text-foreground" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  ON-CHAIN HISTORY
                </h1>
                <p className="text-[10px] text-muted-foreground mt-1 uppercase">
                  VERIFIED BLOCKCHAIN TRANSACTIONS
                </p>
              </div>
            </div>
          </div>
          <div className="w-full space-y-4">
            {loading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
                <div className="text-[10px] text-muted-foreground uppercase">
                  LOADING TRANSACTIONS...
                </div>
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8 text-[10px] text-muted-foreground uppercase">
                NO TRANSACTIONS FOUND
              </div>
            ) : (
              <div className="space-y-2">
                {transactions
                  .slice(0, showAll ? transactions.length : 4)
                  .map((tx) => {
                    const explorerUrl = getExplorerUrl(
                      tx.txHash || "",
                      tx.chain || "solana"
                    );
                    const typeLabel = getTypeLabel(tx.type);
                    const typeColor = getTypeColor(tx.type);

                    return (
                      <div
                        key={tx.id}
                        className="flex items-center justify-between p-4 rounded-lg border border-border/50 hover:border-border hover:bg-muted/30 transition-all"
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                            {getTransactionIcon(tx.type)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] text-muted-foreground uppercase">
                              {new Date(tx.timestamp).toLocaleString()}
                            </div>
                            <div className="text-[8px] text-muted-foreground mt-1">
                              {tx.type === "deposit"
                                ? `From: ${tx.from_address?.slice(0, 6)}...`
                                : tx.type === "withdraw"
                                ? `To: ${tx.to_address?.slice(0, 6)}...`
                                : tx.type === "swap" &&
                                  tx.swapFromSymbol &&
                                  tx.swapToSymbol
                                ? `Swap ${tx.swapFromSymbol} → ${tx.swapToSymbol}`
                                : ""}
                              {explorerUrl && (
                                <a
                                  href={explorerUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-2 hover:text-yellow-400"
                                >
                                  <ExternalLink className="w-3 h-3 inline" />
                                </a>
                              )}
                            </div>
                            <div className="text-[7px] text-muted-foreground mt-0.5">
                              {CHAIN_DISPLAY_NAMES[tx.chain || "solana"]}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-[8px] text-foreground whitespace-nowrap">
                            {formatAmount(tx)}
                          </div>
                          <div className={`text-[10px] font-semibold uppercase ${typeColor}`}>
                            {typeLabel}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                {transactions.length > 4 && (
                  <button
                    onClick={() => setShowAll(!showAll)}
                    className="w-full flex items-center justify-center gap-2 text-[10px] uppercase text-yellow-400 hover:text-yellow-500 transition py-2"
                  >
                    {showAll ? "SHOW LESS" : "MORE DETAIL"}{" "}
                    {showAll ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )}
                  </button>
                )}
              </div>
            )}
            {!loading && transactions.length > 0 && (
              <div className="text-center pt-4">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="text-[10px] text-muted-foreground hover:text-foreground transition uppercase"
                >
                  {refreshing ? "REFRESHING..." : "REFRESH"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
